import type { RouteLocationRaw, RouteMeta } from "vue-router";

import type { ConditionFn, MetaValue } from "@/lib/tellscale/router-guard/types.ts";


/**
 * Represents an individual redirect rule with chainable condition methods
 *
 * Rules support:
 * - Multiple condition checks (all must pass - AND logic)
 * - Meta property validation with optional defaults
 * - Automatic redirect loop prevention
 */
export class RedirectRule {
    private readonly redirect: RouteLocationRaw;

    private readonly currentPath: string;

    private conditions: ConditionFn[] = [];

    private metaChecks: Array<{
        key: string;
        value: MetaValue;
        passIfUndefined?: boolean;
    }> = [];

    /**
     * Creates a new redirect rule
     *
     * @param redirect - The route location to redirect to
     * @param currentPath - The current route path (for loop prevention)
     */
    constructor(redirect: RouteLocationRaw, currentPath: string) {
        this.redirect = redirect;
        this.currentPath = currentPath;
    }

    /**
     * Adds a condition that must be true for the redirect to occur
     * Multiple conditions are AND'ed together
     *
     * @param fn - Function returning boolean condition
     * @returns this - for method chaining
     *
     * @example
     * rule.checkCondition(() => !auth.isAuthenticated)
     */
    public checkCondition = (fn: ConditionFn): this => {
        this.conditions.push(fn);
        return this;
    }

    /**
     * Checks if a route meta-property matches the expected value
     * Supports a boolean flag to control behavior when meta-property is undefined
     *
     * @param key - The meta-property key to check
     * @param value - The expected value
     * @param passIfUndefined - Optional boolean: true = pass check if undefined, false = fail check if undefined
     * @returns this - for method chaining
     */
    public checkMeta = (key: string, value: MetaValue, passIfUndefined?: boolean): this => {
        this.metaChecks.push({ key, value, passIfUndefined });
        return this;
    }

    /**
     * Evaluates if this rule should trigger a redirect
     *
     * @param routeMeta - The target route's metaobject
     * @returns true if all conditions pass and redirect should occur
     */
    public shouldRedirect = (routeMeta: RouteMeta): boolean => {
        // Automatic loop prevention: don't redirect if already on the target path
        if(this.currentPath === (this.redirect as any).path) {
            return false;
        }

        // Validate all meta checks
        for(const check of this.metaChecks) {
            const metaValue = routeMeta[check.key];

            // Handle undefined meta values
            if(metaValue === undefined) {
                // If passIfUndefined is explicitly set, use that value
                // Otherwise, fail the check (default behavior)
                if(check.passIfUndefined !== true) {
                    return false;
                }
            }
            else {
                // Meta-value exists, check if it matches the expected value
                if(metaValue !== check.value) {
                    return false;
                }
            }
        }

        // Validate all conditions (all must pass - AND logic)
        for(const condition of this.conditions) {
            if(!condition()) {
                return false;
            }
        }

        return true;
    }

    /**
     * Gets the redirect location for this rule
     *
     * @returns The route location to redirect to
     */
    public getRedirect = (): RouteLocationRaw => {
        return this.redirect;
    }
}